import React, { useEffect, useMemo, useState } from "react";
import { Container } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faCrown, faLock } from "@fortawesome/free-solid-svg-icons";
import { getCatalogoPlanos, formatarPreco } from "../api/planosApi";
import { usePlan } from "../context/PlanContext";
import "./PlanosPage.css";

const EMAIL_SUPORTE = "contato@leveluptodo.com.br";

const PlanosPage = () => {
  const [searchParams] = useSearchParams();
  const { plano: planoAtual, carregando: carregandoPlano, recarregar } = usePlan();

  const [planos, setPlanos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");

  const recursoBloqueado = searchParams.get("recurso");

  useEffect(() => {
    let ativo = true;

    (async () => {
      try {
        const records = await getCatalogoPlanos();
        if (ativo) setPlanos(records);
      } catch (error) {
        console.error("Erro ao carregar planos:", error);
        if (ativo) setErro("Nao foi possivel carregar os planos agora.");
      } finally {
        if (ativo) setCarregando(false);
      }
    })();

    return () => {
      ativo = false;
    };
  }, []);

  // O nome bonito do recurso vem do proprio catalogo: nao duplicamos a lista aqui.
  const nomeDoRecursoBloqueado = useMemo(() => {
    if (!recursoBloqueado) return null;

    for (const plano of planos) {
      const encontrado = (plano.recursos || []).find((r) => r.codigo === recursoBloqueado);
      if (encontrado) return { nome: encontrado.nome, plano };
    }

    return null;
  }, [recursoBloqueado, planos]);

  const solicitarUpgrade = (plano) => {
    const assunto = encodeURIComponent(`Quero assinar o plano ${plano.nome}`);
    const corpo = encodeURIComponent(
      `Ola! Quero mudar minha conta do LevelUp ToDo para o plano ${plano.nome}.`
    );
    window.location.href = `mailto:${EMAIL_SUPORTE}?subject=${assunto}&body=${corpo}`;
  };

  return (
    <Container className="planos-page">
      <header className="planos-header">
        <h1>Planos</h1>
        <p>Comece de graca. Suba quando fizer sentido.</p>
      </header>

      {nomeDoRecursoBloqueado && (
        <div className="planos-aviso">
          <FontAwesomeIcon icon={faLock} />
          <span>
            <strong>{nomeDoRecursoBloqueado.nome}</strong> faz parte do plano{" "}
            <strong>{nomeDoRecursoBloqueado.plano.nome}</strong>. Faca o upgrade para liberar.
          </span>
        </div>
      )}

      {planoAtual?.status === "expirado" && (
        <div className="planos-aviso planos-aviso-alerta">
          <FontAwesomeIcon icon={faLock} />
          <span>Sua assinatura venceu e a conta voltou para o plano Basico.</span>
        </div>
      )}

      {erro && <div className="planos-erro">{erro}</div>}

      {carregando || carregandoPlano ? (
        <p className="planos-carregando">Carregando planos...</p>
      ) : (
        <div className="planos-grid">
          {planos.map((plano) => {
            const atual = plano.id === planoAtual?.id;
            const abaixoDoAtual = plano.nivel < (planoAtual?.nivel ?? 0);

            return (
              <article
                key={plano.id}
                className={`plano-card${atual ? " plano-card-atual" : ""}${
                  plano.nivel === 1 ? " plano-card-destaque" : ""
                }`}
              >
                {atual && (
                  <span className="plano-flag">
                    <FontAwesomeIcon icon={faCrown} /> Seu plano
                  </span>
                )}

                <div className="plano-topo">
                  <h2>{plano.nome}</h2>
                  <p className="plano-descricao">{plano.descricao}</p>
                  <p className="plano-preco">
                    {formatarPreco(plano.preco_centavos)}
                    <span>{plano.preco_centavos > 0 ? "/mes" : " para sempre"}</span>
                  </p>
                </div>

                <ul className="plano-recursos">
                  {(plano.recursos || []).map((recurso) => (
                    <li
                      key={recurso.codigo}
                      className={recurso.codigo === recursoBloqueado ? "plano-recurso-alvo" : ""}
                    >
                      <FontAwesomeIcon icon={faCheck} /> {recurso.nome}
                    </li>
                  ))}
                </ul>

                {atual ? (
                  <button type="button" className="plano-botao plano-botao-atual" disabled>
                    Plano atual
                  </button>
                ) : abaixoDoAtual ? (
                  <button
                    type="button"
                    className="plano-botao plano-botao-secundario"
                    onClick={() => solicitarUpgrade(plano)}
                  >
                    Falar com a gente
                  </button>
                ) : (
                  <button
                    type="button"
                    className="plano-botao"
                    onClick={() => solicitarUpgrade(plano)}
                  >
                    Quero o {plano.nome}
                  </button>
                )}
              </article>
            );
          })}
        </div>
      )}

      <p className="planos-rodape">
        Ja assinou e o acesso nao liberou?{" "}
        <button type="button" className="planos-link" onClick={recarregar}>
          Atualizar meu plano
        </button>
      </p>
    </Container>
  );
};

export default PlanosPage;
